import { courts } from '@debbie/common/dist/court/index.js';
import { getDebtorsOnCustomer, } from '@debbie/common/dist/customers/index.js';
import { addressToString } from '@debbie/common/dist/index.js';
import { useCase, useCaseList } from '@debbie/cortex/dist/cases/index.js';
import { useCustomer } from '@debbie/cortex/dist/customers/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../../../../theme.js';
import { Col, Row } from '../../../../Grid/index.js';
import Check from '../../../../Icons/Check.js';
import { Input } from '../../../../index.js';
const Rubrik2 = (props) => {
    const lang = useSelector((s) => s.lang);
    const customerLoader = useCustomer(props.singleCase?.customerId);
    const customer = h.hasData(customerLoader) ? customerLoader.data : null;
    const currentDebtors = useMemo(() => (customer ? getDebtorsOnCustomer(customer) : []), [customer]);
    const associationId = props.singleCase?.associationId || null;
    const [extraDebtorsByCase, setExtraDebtorsByCase] = useState({});
    const reportAssociatedDebtors = useCallback((caseId, caseSeqId, debtors) => {
        setExtraDebtorsByCase((prev) => {
            const existing = prev[caseId];
            const sameSize = existing && existing.debtors.length === debtors.length;
            const sameIds = sameSize &&
                existing.debtors.every((d, i) => d.userOnCustomerId === debtors[i].userOnCustomerId);
            if (sameIds) {
                return prev;
            }
            return { ...prev, [caseId]: { caseSeqId, debtors } };
        });
    }, []);
    const allDebtors = useMemo(() => {
        const seen = new Set();
        const result = [];
        const currentSeqId = props.singleCase?.id ?? 0;
        currentDebtors.forEach((debtor) => {
            if (seen.has(debtor.userOnCustomerId)) {
                return;
            }
            seen.add(debtor.userOnCustomerId);
            result.push({ debtor, caseSeqId: currentSeqId, isCurrent: true });
        });
        Object.values(extraDebtorsByCase).forEach(({ caseSeqId, debtors }) => {
            debtors.forEach((debtor) => {
                if (seen.has(debtor.userOnCustomerId)) {
                    return;
                }
                seen.add(debtor.userOnCustomerId);
                result.push({ debtor, caseSeqId, isCurrent: false });
            });
        });
        return result;
    }, [currentDebtors, extraDebtorsByCase, props.singleCase?.id]);
    const defaultSelectedIds = useMemo(() => {
        const primary = currentDebtors.find((d) => d.userOnCustomerId === props.singleCase?.userOnCustomerId);
        if (primary) {
            return [primary.userOnCustomerId];
        }
        return currentDebtors.map((d) => d.userOnCustomerId);
    }, [currentDebtors, props.singleCase?.userOnCustomerId]);
    const effectiveSelectedIds = props.debtorUserOnCustomerIds !== null
        ? props.debtorUserOnCustomerIds
        : defaultSelectedIds;
    const showSelector = allDebtors.length > 1 || associationId !== null;
    const associatedDebtorLoaders = associationId &&
        props.singleCase &&
        customer?.creditorId && (React.createElement(AssociatedCaseDebtorsLoaders, { associationId: associationId, currentCaseId: props.singleCase.caseId, onLoaded: reportAssociatedDebtors }));
    if (!showSelector && !customer?.isCompany) {
        return null;
    }
    const isSelected = (id) => effectiveSelectedIds.includes(id);
    const toggle = (id) => {
        const next = isSelected(id)
            ? effectiveSelectedIds.filter((x) => x !== id)
            : [...effectiveSelectedIds, id];
        props.setDocumentState({ debtorUserOnCustomerIds: next });
    };
    const allSelected = allDebtors.length > 0 &&
        effectiveSelectedIds.length === allDebtors.length &&
        allDebtors.every((e) => effectiveSelectedIds.includes(e.debtor.userOnCustomerId));
    const toggleAll = () => {
        props.setDocumentState({
            debtorUserOnCustomerIds: allSelected
                ? []
                : allDebtors.map((e) => e.debtor.userOnCustomerId),
        });
    };
    const showCaseBadge = allDebtors.some((e) => !e.isCurrent);
    return (React.createElement(Row, null,
        React.createElement(Col, { width: "12" },
            React.createElement("h3", null, "Rubrik 2"),
            associatedDebtorLoaders,
            showSelector && (React.createElement(Section, null,
                React.createElement(Header, null,
                    React.createElement(HeaderText, null,
                        React.createElement(Title, null, lang.documents.selectDebtors),
                        React.createElement(Description, null, lang.documents.selectDebtorsDescription)),
                    React.createElement(HeaderActions, null,
                        React.createElement(Counter, null,
                            effectiveSelectedIds.length,
                            "/",
                            allDebtors.length,
                            ' ',
                            lang.documents.debtorsSelected),
                        React.createElement(ToggleAllButton, { type: "button", onClick: toggleAll }, allSelected
                            ? lang.documents.deselectAll
                            : lang.documents.selectAll))),
                React.createElement(CardGrid, null, allDebtors.map(({ debtor, caseSeqId, isCurrent }) => {
                    const court = debtor.details.courtId &&
                        courts.find((c) => c.id === debtor.details.courtId);
                    return (React.createElement(DebtorCard, { key: debtor.userOnCustomerId, selected: isSelected(debtor.userOnCustomerId), onClick: () => toggle(debtor.userOnCustomerId), type: "button", "aria-pressed": isSelected(debtor.userOnCustomerId) },
                        React.createElement(CheckIndicator, { selected: isSelected(debtor.userOnCustomerId) }, isSelected(debtor.userOnCustomerId) && (React.createElement(Check, { size: "12", color: "white" }))),
                        React.createElement(DebtorBody, null,
                            court && (React.createElement(MetaLine, null,
                                lang.documents.court,
                                ": ",
                                court.name)),
                            React.createElement(DebtorHeading, null,
                                React.createElement(DebtorName, null, debtor.details.name || lang.debtors.debtor),
                                showCaseBadge && (React.createElement(CaseBadge, { isCurrent: isCurrent },
                                    "#",
                                    caseSeqId))),
                            React.createElement(DebtorMeta, null,
                                debtor.details.cpr && (React.createElement(MetaLine, null,
                                    "CPR: ",
                                    debtor.details.cpr)),
                                debtor.details.cvr && (React.createElement(MetaLine, null,
                                    "CVR: ",
                                    debtor.details.cvr)),
                                debtor.details.address?.address && (React.createElement(MetaLine, null, addressToString(debtor.details.address, {
                                    noCountry: true,
                                }))),
                                debtor.details.email && (React.createElement(MetaLine, { muted: true }, debtor.details.email))))));
                })))),
            customer?.isCompany && (React.createElement(React.Fragment, null,
                React.createElement("p", null, lang.documents.directorNameAddressDescription),
                React.createElement(Input, { placeholder: lang.documents.directorNameAddress, value: props.directorNameAddress, onChange: (e) => {
                        props.setDocumentState({
                            directorNameAddress: e.target.value,
                        });
                    } }))))));
};
export default Rubrik2;
const AssociatedCaseDebtorsLoaders = (props) => {
    const associatedCasesLoader = useCaseList({}, 0, 100, {
        associationId: props.associationId,
    });
    const associatedCases = h.hasData(associatedCasesLoader)
        ? associatedCasesLoader.data.items.filter((c) => c.caseId !== props.currentCaseId)
        : [];
    return (React.createElement(React.Fragment, null, associatedCases.map((c) => (React.createElement(AssociatedCaseDebtorsLoader, { key: c.caseId, caseId: c.caseId, caseSeqId: c.id, onLoaded: props.onLoaded })))));
};
const AssociatedCaseDebtorsLoader = (props) => {
    const caseLoader = useCase(props.caseId);
    const caseData = h.hasData(caseLoader) ? caseLoader.data : null;
    const customerLoader = useCustomer(caseData?.customerId);
    const customer = h.hasData(customerLoader) ? customerLoader.data : null;
    const debtors = useMemo(() => (customer ? getDebtorsOnCustomer(customer) : []), [customer]);
    useEffect(() => {
        if (!customer) {
            return;
        }
        props.onLoaded(props.caseId, props.caseSeqId, debtors);
    }, [customer, debtors, props.caseId, props.caseSeqId, props.onLoaded]);
    return null;
};
const Section = styled.div `
  margin-bottom: 24px;
`;
const Header = styled.div `
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;
const HeaderText = styled.div `
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const Title = styled.div `
  font-weight: 600;
  font-size: 15px;
  color: ${THEME.COLOR_PRIMARY};
`;
const Description = styled.div `
  font-size: 13px;
  color: ${THEME.DARK_COLOR_SECONDARY};
`;
const HeaderActions = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
`;
const Counter = styled.span `
  font-size: 12px;
  color: ${THEME.DARK_COLOR_SECONDARY};
  white-space: nowrap;
`;
const ToggleAllButton = styled.button `
  background: transparent;
  border: 1px solid ${THEME.COLOR_ACCENT};
  color: ${THEME.COLOR_ACCENT};
  font-size: 12px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s ease-in-out;

  &:hover {
    background: ${THEME.COLOR_ACCENT}14;
  }
`;
const CardGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
`;
const DebtorCard = styled.button `
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  padding: 14px;
  border-radius: 8px;
  border: 1.5px solid
    ${({ selected }) => (selected ? THEME.COLOR_ACCENT : '#E2E5EE')};
  background: ${({ selected }) => selected ? `${THEME.COLOR_ACCENT}0D` : '#FFFFFF'};
  cursor: pointer;
  transition:
    border-color 0.12s ease-in-out,
    background 0.12s ease-in-out,
    box-shadow 0.12s ease-in-out;
  box-shadow: ${({ selected }) => selected ? `0 0 0 3px ${THEME.COLOR_ACCENT}1F` : 'none'};

  &:hover {
    border-color: ${THEME.COLOR_ACCENT};
  }
`;
const CheckIndicator = styled.span `
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid
    ${({ selected }) => (selected ? THEME.COLOR_ACCENT : '#C7CCDA')};
  background: ${({ selected }) => (selected ? THEME.COLOR_ACCENT : '#FFFFFF')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  transition:
    background 0.12s ease-in-out,
    border-color 0.12s ease-in-out;
`;
const DebtorBody = styled.div `
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;
const DebtorHeading = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
const DebtorName = styled.div `
  font-weight: 600;
  font-size: 14px;
  color: ${THEME.COLOR_PRIMARY};
  word-break: break-word;
  flex: 1;
  min-width: 0;
`;
const CaseBadge = styled.span `
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ isCurrent }) => isCurrent ? `${THEME.COLOR_ACCENT}1F` : '#EEF0F6'};
  color: ${({ isCurrent }) => isCurrent ? THEME.COLOR_ACCENT : THEME.DARK_COLOR_SECONDARY};
`;
const DebtorMeta = styled.div `
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const MetaLine = styled.div `
  font-size: 12px;
  color: ${({ muted }) => muted ? THEME.DARK_COLOR_SECONDARY : THEME.COLOR_PRIMARY};
  word-break: break-word;
`;
//# sourceMappingURL=Rubrik2.js.map